import React from 'react';
import { Typography, Box, Chip, IconButton, Tooltip } from '@mui/material';
import { FaChartBar, FaCalendarCheck, FaUser, FaFileAlt } from 'react-icons/fa';
import { COLORS } from '../../../constants/radar';
import { SessionStatusChip } from '../../common/SessionStatusChip';
import { getRoleChipStyle } from '../../../constants/roleTypes';
import {
  getAssignmentStatus,
  canViewAssignmentReport,
  getAssignmentStatusColor,
  getAssignmentStatusText,
  getAssignmentStatusBorderColor,
  getAssignmentStatusTextColor,
  ASSIGNMENT_STATUSES,
} from '../../../constants/assignmentStatuses';
import {
  getReviewTypeColor,
  getReviewTypeTextColor,
  getReviewTypeText
} from '../../../constants/reviewTypes';
import { ReviewTypeBadge } from '../../common/ReviewTypeBadge';
import { AssignmentFrozenBadge } from '../../common/AssignmentFrozenBadge';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import {
  SessionCard,
  SessionCardContent,
  SessionDetails,
} from '../../FeedbackSessions/styles';

const tooltipConfig = {
  arrow: true,
  placement: 'top',
  enterDelay: 500,
  leaveDelay: 200
};

/**
 * MyFeedbackResultCardItem - Card component for displaying feedback result assignments
 * Used in My Feedback Results grid to show assignments where the user is an employee being reviewed
 */
const MyFeedbackResultCardItem = ({
  assignment,
  onViewReport
}) => {
  const session = assignment.feedbackSession;
  const isIndividualAssignment = !session;
  const individualReviewType = assignment.reviewers?.[0]?.reviewType;


  const personalFrozen = !!session?.completedAt
    && getAssignmentStatus(assignment) !== ASSIGNMENT_STATUSES.COMPLETED;

  const matrix = assignment.skillsMatrix;
  const reviewProcess = assignment.reviewProcess?.[0];

  // Helper function to determine if view report should be enabled

  const viewReportEnabled = canViewAssignmentReport(assignment);

  return (
    <SessionCard>
      <SessionCardContent>
        {/* Employee Avatar and Full Name */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          mb: 2,
          mt: 1
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <EmployeeAvatar
                  employee={assignment.employee}
                  size={40}
                />
                <Box>
                  <Typography sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: COLORS.PRIMARY,
                    lineHeight: 1.3
                  }}>
                    {assignment.employee?.fullName || 'Unknown Employee'}
                  </Typography>
                  <Typography sx={{
                    fontSize: '12px',
                    color: '#64748B',
                    lineHeight: 1.3
                  }}>
                    {assignment.employee?.orgPositions?.label || 'No Position'}
                    {assignment.employee?.orgGrades?.label &&
                      <> • {assignment.employee.orgGrades.label}</>
                    }
                  </Typography>
                </Box>
              </Box>

              {personalFrozen ? (
                <AssignmentFrozenBadge status={getAssignmentStatus(assignment)} />
              ) : (
                <Chip
                  label={getAssignmentStatusText(assignment)}
                  sx={{
                    backgroundColor: getAssignmentStatusColor(assignment),
                    color: getAssignmentStatusTextColor(assignment),
                    fontWeight: 500,
                    fontSize: '13px',
                    border: '1px solid',
                    borderColor: getAssignmentStatusBorderColor(assignment),
                    '& .MuiChip-label': {
                      px: 2
                    }
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{
          borderTop: '1px solid #E2E8F0',
          pt: 2,
          mt: 1
        }} />

        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          mb: 2,
          mt: 1
        }}>
          <Typography sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#64748B',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            {isIndividualAssignment && <FaUser size={12} style={{ color: '#64748B' }} />}
            {isIndividualAssignment ? 'Individual Assignment' : 'Feedback Session'}
          </Typography>
          {isIndividualAssignment && individualReviewType && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mt: 1
            }}>
              <ReviewTypeBadge
                reviewType={individualReviewType}
                getReviewTypeColor={getReviewTypeColor}
                getReviewTypeTextColor={getReviewTypeTextColor}
                getReviewTypeText={getReviewTypeText}
              />
            </Box>
          )}

          {!isIndividualAssignment && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}>
              <Box>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1
                }}>
                  <Typography sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: COLORS.PRIMARY,
                    lineHeight: 1.3
                  }}>
                    {session.name}
                  </Typography>

                  {/* ADR-014 §10: ONE chip carries both the lifecycle state and how
                      the session was closed — see common/SessionStatusChip. */}
                  <SessionStatusChip session={session} />
                </Box>

                <Typography sx={{
                  fontSize: '12px',
                  color: '#64748B',
                  lineHeight: 1.3,
                  mt: 0.5
                }}>
                  {session.businessUnit.name}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mt: 1
              }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <FaCalendarCheck size={14} style={{ color: '#94A3B8' }} />
                  <Typography sx={{
                    fontSize: '14px',
                    color: '#64748B',
                  }}>
                    {`Session of ${session.month}/${session.year}`}
                  </Typography>
                </Box>

                <ReviewTypeBadge
                  reviewType={session.reviewType}
                  getReviewTypeColor={getReviewTypeColor}
                  getReviewTypeTextColor={getReviewTypeTextColor}
                  getReviewTypeText={getReviewTypeText}
                />
              </Box>
            </Box>
          )}

          {/* Session Template */}
          {reviewProcess && reviewProcess.skillsMatrixSession && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              mt: 0,
              fontSize: '13px',
              color: '#64748B',
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <FaFileAlt size={14} style={{ color: '#94A3B8' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '14px' }}>
                    {reviewProcess.skillsMatrixSession.name}
                  </Typography>
                  {reviewProcess.skillsMatrixSession.version && (
                    <Chip
                      label={`v${reviewProcess.skillsMatrixSession.version}`}
                      size="small"
                      sx={{
                        fontSize: '11px',
                        height: '20px',
                        backgroundColor: '#F1F5F9',
                        color: '#475569',
                        border: '1px solid #E2E8F0',
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{
          borderTop: '1px solid #E2E8F0',
          pt: 2,
          mt: 1
        }} />

        <SessionDetails>
          {/* Skills Matrix Applied section */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mb: 2
          }}>
            <Box>
              <Typography sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#64748B',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 0.5
              }}>
                Skills Matrix Applied
              </Typography>
              <Typography sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: COLORS.PRIMARY,
                lineHeight: 1.3,
                mb: 2
              }}>
                {matrix.name}
              </Typography>

              <Typography sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#64748B',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 0.5
              }}>
                Position
              </Typography>
              <Box sx={{
                display: 'flex',
                mb: 2
              }}>
                <Chip
                  label={matrix.position.label}
                  size="small"
                  sx={{
                    fontSize: '13px',
                    backgroundColor: '#F1F5F9',
                    color: '#475569',
                    border: '1px solid #E2E8F0',
                    height: '24px',
                    '& .MuiChip-label': {
                      px: 1.5
                    }
                  }}
                />
              </Box>

              {matrix.matrixRoles && matrix.matrixRoles.length > 0 && (
                <>
                  <Typography sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#64748B',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 1
                  }}>
                    Roles
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {matrix.matrixRoles.map(role => {
                      const { bgColor, textColor } = getRoleChipStyle(role.role.type);
                      return (
                        <div
                          key={role.id}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '16px',
                            backgroundColor: bgColor,
                            fontSize: '13px',
                            color: textColor,
                            fontWeight: '500',
                            border: `1px solid ${textColor}20`,
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}
                        >
                          {role.role.label}
                        </div>
                      );
                    })}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </SessionDetails>

        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 3,
          pt: 3,
          borderTop: '1px solid #E2E8F0',
          gap: 1
        }}>
          <Tooltip
            title="View feedback report"
            {...tooltipConfig}
          >
            <IconButton
              size="small"
              onClick={() => onViewReport(assignment.id)}
              disabled={!viewReportEnabled}
              sx={{
                backgroundColor: viewReportEnabled ? '#F0F7FF' : '#F1F5F9',
                border: '1px solid',
                borderColor: viewReportEnabled ? '#BFDBFE' : '#E2E8F0',
                opacity: viewReportEnabled ? 1 : 0.5,
                cursor: viewReportEnabled ? 'pointer' : 'not-allowed',
                '&:hover': {
                  backgroundColor: viewReportEnabled ? '#DBEAFE' : '#F1F5F9',
                }
              }}
            >
              <FaChartBar size={14} color={viewReportEnabled ? "#3B82F6" : "#94A3B8"} />
            </IconButton>
          </Tooltip>
        </Box>
      </SessionCardContent>
    </SessionCard>
  );
};

export default MyFeedbackResultCardItem;
